import React, { useEffect, useState } from 'react';
import { CustomDialog } from 'components/CustomDialog';
import { Typography } from '@mui/material';
import { CookieCategoryBlock } from './CookieCategoryBlock';
import { CategoriesList, InlineTextButton, SettingsContent } from './styled-components';
import { ButtonContained } from 'components/Button';
import { PreviewContractDialog } from 'components/PreviewContract/PreviewContract';
import { getCookieConsent, saveCookieConsent } from 'utils/cookieConsent';

interface CookieSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  onSave?: () => void;
}

type OptionalCookieCategoryId = 'analytics' | 'marketing';

const COOKIE_CATEGORIES = [
  {
    id: 'necessary',
    title: 'Cookies obligatorias',
    description:
      'Son esenciales y le ayudarán a navegar, moverse por el sitio web y ver ciertas características. Estas cookies son obligatorias para permitir el funcionamiento básico de nuestras plataformas. Estas cookies se guardan mientras dure su sesión de navegación',
    locked: true,
  },
  {
    id: 'analytics',
    title: 'Cookies de funcionalidad y analíticas',
    description:
      'Están presentes para que podamos proporcionarte una experiencia de cliente aún mejor. Estas cookies nos permiten analizar el uso de nuestras plataformas para que podamos medir y mejorar el rendimiento. Además, las podemos colocar nosotros o un tercero en nuestro nombre y se almacenan mientras dure tu sesión de navegación.',
    locked: false,
  },
  {
    id: 'marketing',
    title: 'Cookies publicitarias y de redes sociales',
    description:
      'Se recordarán tus preferencias de productos y compras las cuales nos ayudan de otras formas en las iniciativas de marketing. Estas cookies nos permiten compartir datos, como lo que te gusta, con nuestros anunciantes, de manera que la publicidad que vea pueden ser más relevante para sus preferencias (a veces conocidas como “cookies de segmentación”). Nos ayudan a conocer los hábitos de compra de los visitantes. Esto nos permite seguir mejorando nuestras plataformas, para que las compras resulten más fáciles y más agradables, y para mejorar los mensajes de marketing que dirigimos a nuestros clientes (a veces conocidas como “cookies de rendimiento”). Se utilizan para que puedas expresar tu opinión. Estas cookies te permitirán darnos un “Me gusta” o recomendarnos en las redes sociales y para chatear con nuestros agentes de servicio. Pero también puedes aconsejar a nuestros otros clientes (potenciales) calificando y revisando nuestros productos (a veces conocidas como “cookies de interacción”).',
    locked: false,
  },
] as const;

export const CookieSettingsDialog = ({ open, onClose, onSave }: CookieSettingsDialogProps) => {
  const [optionalConsent, setOptionalConsent] = useState<Record<OptionalCookieCategoryId, boolean>>({
    analytics: false,
    marketing: false,
  });
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [staticDocUrl, setStaticDocUrl] = useState('');
  const [contractLoading, setContractLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    const existingConsent = getCookieConsent();
    setOptionalConsent({
      analytics: existingConsent?.analytics ?? false,
      marketing: existingConsent?.marketing ?? false,
    });
  }, [open]);

  const handleCategoryToggle: (id: OptionalCookieCategoryId, checked: boolean) => void = (id, checked) => {
    setOptionalConsent(prevState => ({
      ...prevState,
      [id]: checked,
    }));
  };

  const handleOpenPrivacyPolicy: () => void = () => {
    setShowPreviewDialog(true);
    setStaticDocUrl(`${window.location.origin}${process.env.PUBLIC_URL}/legals/privacy_policy`);
  };

  const handleClosePreviewDialog: () => void = () => {
    setShowPreviewDialog(false);
    setStaticDocUrl('');
  };

  const handleViewContract: (endpoint: string) => Promise<void> = async endpoint => {
    setContractLoading(true);
    try {
      const data = await fetch(endpoint);
      const blobbedData = await data.blob();
      const url = URL.createObjectURL(blobbedData);
      const a = window.document.createElement('a');
      a.href = url;
      const fileName = endpoint.substring(endpoint.lastIndexOf('/') + 1);
      a.download = fileName;
      window.document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 10000);
    } finally {
      setContractLoading(false);
    }
  };

  const getCategoryState = (category: (typeof COOKIE_CATEGORIES)[number]) => {
    if (category.locked) {
      return {
        checked: true,
        disabled: true,
      };
    }

    return {
      checked: optionalConsent[category.id],
      disabled: false,
      onToggle: (checked: boolean) => {
        handleCategoryToggle(category.id, checked);
      },
    };
  };

  const handleDownloadPrivacyPolicy: () => void = () => {
    handleViewContract(`${staticDocUrl}.pdf`);
  };

  const handleSavePreferences: () => void = () => {
    saveCookieConsent({
      analytics: optionalConsent.analytics,
      marketing: optionalConsent.marketing,
    });
    onSave?.();
    onClose();
  };

  return (
    <>
      <CustomDialog
        open={open}
        title="Configurador de cookies"
        maxWidth="md-lg"
        onClose={onClose}
        bodyChildren={
          <SettingsContent>
            <Typography>
              Si lo autorizas, cuando visites nuestro sitio web almacenaremos o recuperaremos información en el
              navegador a través de cookies no necesarias. La información que captemos permitirá que el sitio funcione
              como esperas que lo haga y se adecue de mejor manera a tus preferencias. El hecho de no autorizar algunos
              tipos de cookies puede afectar tu experiencia en nuestros sitios, así como los servicios que te podemos
              ofrecer. Aquí debajo encontrarás el detalle:
            </Typography>
            <CategoriesList>
              {COOKIE_CATEGORIES.map(category => (
                <CookieCategoryBlock
                  key={category.id}
                  title={category.title}
                  description={category.description}
                  {...getCategoryState(category)}
                />
              ))}
            </CategoriesList>
            <Typography component="div">
              Si lo autorizas, podremos aplicar nuestra{' '}
              <InlineTextButton variant="text" color="primary" onClick={handleOpenPrivacyPolicy}>
                Política de Privacidad
              </InlineTextButton>{' '}
              al tratamiento de la información que captemos.
            </Typography>
          </SettingsContent>
        }
        buttonsChildren={<ButtonContained label={'Guardar preferencias'} onClick={handleSavePreferences} />}
      />
      {showPreviewDialog && (
        <PreviewContractDialog
          open={showPreviewDialog}
          staticDoc
          canDownLoad
          url={`${staticDocUrl}.html`}
          contractLoading={contractLoading}
          onClose={handleClosePreviewDialog}
          onDownload={handleDownloadPrivacyPolicy}
        />
      )}
    </>
  );
};
